import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from '@/lib/toast';
import { usePlanLimits } from './usePlanLimits';
import { supabase } from '@/integrations/supabase/client';
import { CACHE_KEYS } from '@/lib/cacheUtils';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export interface SaveRigConnectionData {
  sourceDeviceId: string;
  sourcePortType: string;
  sourcePortIndex?: number;
  targetDeviceId: string;
  targetPortType: string;
  targetPortIndex?: number;
  connectionType: 'power' | 'video';
  isDirect?: boolean;
}

// Alias for real-time connection commands
export type AddConnectionData = SaveRigConnectionData;

export interface SaveRigData {
  cameraId: string;
  rigStyle: string;
  accessoryIds: string[];
  connections?: SaveRigConnectionData[];
}

export interface RecommendEquipmentData {
  type: 'camera' | 'accessory' | 'battery';
  name: string;
  brand?: string;
  category?: string;
  useCase?: string;
}

export interface CreateTicketData {
  subject: string;
  description: string;
}

export interface RentalHouseSuggestionData {
  name: string;
  url: string;
  country: string;
}

// Parse [SAVE_RIG:{...}] from message content
function parseSaveRigCommand(content: string): SaveRigData | null {
  const match = content.match(/\[SAVE_RIG:(\{[^}]+\})\]/);
  if (!match) return null;
  
  try {
    const data = JSON.parse(match[1]);
    if (data.cameraId && data.rigStyle && Array.isArray(data.accessoryIds)) {
      return data as SaveRigData;
    }
  } catch {
    // Invalid JSON in command - silent fail
  }
  return null;
}

// Parse [RECOMMEND_EQUIPMENT:{...}] from message content
function parseRecommendEquipmentCommand(content: string): RecommendEquipmentData | null {
  const match = content.match(/\[RECOMMEND_EQUIPMENT:(\{[^\]]+\})\]/);
  if (!match) {
    return null;
  }
  
  try {
    const data = JSON.parse(match[1]);
    if (data.type && data.name) {
      return data as RecommendEquipmentData;
    }
  } catch (e) {
    // Silent fail - command parsing is optional
  }
  return null;
}

// Parse [CREATE_TICKET:{...}] from message content
function parseCreateTicketCommand(content: string): CreateTicketData | null {
  const match = content.match(/\[CREATE_TICKET:(\{[^\]]+\})\]/);
  if (!match) return null;
  
  try {
    const data = JSON.parse(match[1]);
    if (data.subject && data.description) {
      return data as CreateTicketData;
    }
  } catch {
    // Invalid JSON in command - silent fail
  }
  return null;
}

// Parse [SAVE_RENTAL_SUGGESTION:{...}] from message content
// Be tolerant to minor formatting issues from the model (extra whitespace, code fences, missing trailing bracket)
function parseRentalSuggestionCommand(content: string): RentalHouseSuggestionData | null {
  // First try the explicit SAVE command
  const tag = '[SAVE_RENTAL_SUGGESTION:';
  const start = content.indexOf(tag);
  
  if (start !== -1) {
    // Prefer a proper closing "]" but tolerate if missing
    const afterTag = content.slice(start + tag.length);
    const endBracket = afterTag.indexOf(']');
    const raw = (endBracket === -1 ? afterTag : afterTag.slice(0, endBracket)).trim();

    // Strip surrounding backticks / code fences if model wrapped it
    const cleaned = raw
      .replace(/^```(json)?/i, '')
      .replace(/```$/i, '')
      .trim();

    // Try to isolate a JSON object
    const firstBrace = cleaned.indexOf('{');
    const lastBrace = cleaned.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      const jsonStr = cleaned.slice(firstBrace, lastBrace + 1);
      try {
        const data = JSON.parse(jsonStr);
        // URL can be empty string - only require name and country
        if (data?.name && data?.country) {
          console.log('[RentalSuggestion] Parsed from SAVE command:', data);
          return {
            name: String(data.name),
            url: String(data.url || ''),
            country: String(data.country),
          };
        }
      } catch (e) {
        console.warn('[RentalSuggestion] Failed to parse SAVE command JSON:', e);
      }
    }
  }

  // FALLBACK: Extract from [RENTAL_SUGGESTION_SUBMITTED]...[/RENTAL_SUGGESTION_SUBMITTED] block
  // This matches the visual block format the AI uses
  const submittedBlockMatch = content.match(/\[RENTAL_SUGGESTION_SUBMITTED\]([\s\S]*?)\[\/RENTAL_SUGGESTION_SUBMITTED\]/);
  if (submittedBlockMatch) {
    const blockContent = submittedBlockMatch[1].trim();
    // Find JSON object within the block content
    const firstBrace = blockContent.indexOf('{');
    const lastBrace = blockContent.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      const jsonStr = blockContent.slice(firstBrace, lastBrace + 1);
      try {
        const data = JSON.parse(jsonStr);
        // URL can be empty string - only require name and country
        if (data?.name && data?.country) {
          console.log('[RentalSuggestion] Parsed from SUBMITTED block (fallback):', data);
          return {
            name: String(data.name),
            url: String(data.url || ''),
            country: String(data.country),
          };
        }
      } catch (e) {
        console.warn('[RentalSuggestion] Failed to parse SUBMITTED block JSON:', e);
      }
    }
  }

  return null;
}

// Parse ALL [ADD_CONNECTION:{...}] commands for real-time signal flow updates
function parseAllAddConnectionCommands(content: string): AddConnectionData[] {
  const connections: AddConnectionData[] = [];
  const tag = '[ADD_CONNECTION:';
  let searchStart = 0;
  
  while (true) {
    const start = content.indexOf(tag, searchStart);
    if (start === -1) break;
    
    const jsonStart = content.indexOf('{', start);
    if (jsonStart === -1) break;
    
    // Find matching closing brace (handle nested objects)
    let depth = 0;
    let jsonEnd = jsonStart;
    for (let i = jsonStart; i < content.length; i++) {
      if (content[i] === '{') depth++;
      else if (content[i] === '}') {
        depth--;
        if (depth === 0) {
          jsonEnd = i;
          break;
        }
      }
    }
    
    const jsonStr = content.slice(jsonStart, jsonEnd + 1);
    try {
      const data = JSON.parse(jsonStr);
      if (data.sourceDeviceId && data.targetDeviceId && data.connectionType) {
        connections.push(data as AddConnectionData);
      }
    } catch {
      // Invalid JSON - skip this occurrence
    }
    
    // Move past this command for next iteration
    searchStart = jsonEnd + 1;
  }
  
  return connections;
}

// Helper to strip ALL nested JSON commands (SAVE_RIG, ADD_CONNECTION) - handles multiple occurrences
function stripNestedJsonCommand(content: string, tag: string): string {
  let result = content;
  
  // Loop to remove ALL occurrences
  while (true) {
    const start = result.indexOf(`[${tag}:`);
    if (start === -1) break;
    
    const jsonStart = result.indexOf('{', start);
    if (jsonStart === -1) break;
    
    let depth = 0;
    let jsonEnd = jsonStart;
    for (let i = jsonStart; i < result.length; i++) {
      if (result[i] === '{') depth++;
      else if (result[i] === '}') {
        depth--;
        if (depth === 0) {
          jsonEnd = i;
          break;
        }
      }
    }
    
    const commandEnd = result.indexOf(']', jsonEnd);
    if (commandEnd === -1) break;
    
    const beforeCommand = result.slice(0, start).replace(/\n+$/, '');
    const afterCommand = result.slice(commandEnd + 1).replace(/^\n+/, '');
    result = beforeCommand + afterCommand;
  }
  
  return result;
}

// Remove action command blocks from display content
// NOTE: Visual blocks like [TICKET_OFFER], [TICKET_CREATED], [RENTAL_SUGGESTION_*] are preserved for MessageContent to render
export function stripSaveRigCommand(content: string): string {
  let result = content;
  
  // Handle SAVE_RIG with nested JSON
  result = stripNestedJsonCommand(result, 'SAVE_RIG');
  
  // Handle ADD_CONNECTION with nested JSON
  result = stripNestedJsonCommand(result, 'ADD_CONNECTION');
  
  return result
    .replace(/\n?\[RECOMMEND_EQUIPMENT:\{[^\]]+\}\]\n?/g, '')
    .replace(/\n?\[CREATE_TICKET:\{[^\]]+\}\]\n?/g, '')
    .replace(/\n?\[SAVE_RENTAL_SUGGESTION:\{[^\]]+\}\]\n?/g, '')
    .trim();
}

// System function patterns - messages matching these are FREE (don't count toward limit)
const SYSTEM_FUNCTION_KEYWORDS = [
  // Initial trigger phrases - start of support workflows (FREE)
  'suggest a rental house',
  'suggest rental house',
  "i'd like to suggest a rental house",
  'recommend equipment',
  'recommend a camera',
  'recommend a lens',
  'recommend an accessory',
  'recommend a tripod',
  'recommend a gimbal',
  'create a support ticket',
  'create support ticket',
  'submit a support ticket',
  'i have a problem',
  'i need help with',
  'report a bug',
  'report an issue',
  
  // Recommendation workflow responses
  'yes, please submit',
  'submit recommendation',
  'yes, submit',
  
  // Ticket workflow responses  
  'create ticket',
  'ticket has been created',
  
  // Rental suggestion responses
  'yes, save it',
  'submit suggestion',
  
  // Confirmation phrases
  'yes, add it',
  'add to recommendations',
];

// Visual blocks that indicate we're in a system function workflow
const SYSTEM_FUNCTION_BLOCKS = [
  '[WHY_QUESTION]',
  '[RECOMMENDATION_OFFER]',
  '[RECOMMENDATION_SENT]',
  '[TICKET_GATHERING]',
  '[TICKET_OFFER]',
  '[TICKET_CREATED]',
  '[RENTAL_SUGGESTION_OFFER]',
  '[RENTAL_SUGGESTION_DETAILS]',
  '[RENTAL_SUGGESTION_SUBMITTED]',
];

// Workflow start blocks - indicate a system function workflow is in progress
const WORKFLOW_START_BLOCKS = [
  '[RECOMMENDATION_OFFER]',
  '[WHY_QUESTION]',
  '[TICKET_GATHERING]',
  '[TICKET_OFFER]',
  '[RENTAL_SUGGESTION_OFFER]',
  '[RENTAL_SUGGESTION_DETAILS]',
];

// Workflow complete blocks - indicate the workflow has finished
const WORKFLOW_COMPLETE_BLOCKS = [
  '[RECOMMENDATION_SENT]',
  '[TICKET_CREATED]',
  '[RENTAL_SUGGESTION_SUBMITTED]',
];

// Check if a user message is part of a system function workflow (these are FREE)
// Now checks the ENTIRE recent conversation context, not just the last message
// 
// WORKFLOW STATE MACHINE:
// - Start blocks ([RECOMMENDATION_OFFER], [TICKET_GATHERING], etc.) → workflow ACTIVE (FREE)
// - Complete blocks ([RECOMMENDATION_SENT], [TICKET_CREATED], etc.) → workflow DONE (next msg COUNTED)
// - Regular rig questions after workflow completes → COUNTED normally
function isSystemFunctionMessage(userMessage: string, previousMessages: Message[]): boolean {
  const lowerInput = userMessage.toLowerCase().trim();
  
  // 1. Check if message matches known system function TRIGGER keywords
  // These always start a FREE workflow
  for (const keyword of SYSTEM_FUNCTION_KEYWORDS) {
    if (lowerInput.includes(keyword.toLowerCase())) {
      console.log('[SystemFunc] Matched keyword trigger:', keyword);
      return true;
    }
  }
  
  // 2. Look at recent assistant messages to detect active workflows
  // This handles multi-turn workflows where the AI asks clarifying questions
  const recentAssistantMessages = previousMessages
    .filter(m => m.role === 'assistant')
    .slice(-5); // Check last 5 assistant messages
  
  if (recentAssistantMessages.length === 0) {
    return false; // No history, not in a workflow
  }
  
  // 3. Track workflow state by scanning messages chronologically
  // Most recent block type determines the state
  let lastWorkflowState: 'none' | 'active' | 'complete' = 'none';
  let lastBlockFound = '';
  
  for (const msg of recentAssistantMessages) {
    // Check for workflow start/continue blocks
    for (const startBlock of WORKFLOW_START_BLOCKS) {
      if (msg.content.includes(startBlock)) {
        lastWorkflowState = 'active';
        lastBlockFound = startBlock;
      }
    }
    
    // Check for completion blocks - these END the workflow
    // Checked AFTER start blocks so if same message has both, complete wins
    for (const completeBlock of WORKFLOW_COMPLETE_BLOCKS) {
      if (msg.content.includes(completeBlock)) {
        lastWorkflowState = 'complete';
        lastBlockFound = completeBlock;
      }
    }
  }
  
  const isActive = lastWorkflowState === 'active';
  
  if (lastWorkflowState !== 'none') {
    console.log('[SystemFunc] Workflow state:', lastWorkflowState, '| Last block:', lastBlockFound, '| Message FREE:', isActive);
  }
  
  // Only return true if workflow is ACTIVE (not complete, not none)
  return isActive;
}

// Export functions for UI component access
export { createSupportTicket, saveRentalHouseSuggestion };

interface ActiveAccessoryInfo {
  id: string;
  name: string;
  category: string;
}

interface RigContext {
  cameraId?: string;
  cameraName?: string;
  rigStyle?: string;
  selectedAccessories?: string[];
  // Active rig info for real-time AI operations
  activeRigId?: string;
  activeProjectId?: string;
  activeAccessories?: ActiveAccessoryInfo[];
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rig-assistant`;
const STORAGE_KEY = CACHE_KEYS.RIG_ASSISTANT_HISTORY;

function loadMessages(): Message[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Failed to load - return empty array
  }
  return [];
}

function saveMessages(messages: Message[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  } catch {
    // Failed to save - storage may be full
  }
}

// Save equipment recommendation to database
async function saveEquipmentRecommendation(data: RecommendEquipmentData) {
  try {
    // Get current user info
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id || null;

    // Check if this equipment already exists in recommendations (to increment count)
    const { data: existing } = await supabase
      .from('equipment_recommendations')
      .select('id, request_count')
      .eq('equipment_name', data.name)
      .eq('equipment_type', data.type)
      .maybeSingle();

    if (existing) {
      // Increment request count for existing recommendation
      await supabase
        .from('equipment_recommendations')
        .update({ 
          request_count: (existing.request_count ?? 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id);
    } else {
      // Insert new recommendation (user_id is used instead of storing email directly)
      await supabase
        .from('equipment_recommendations')
        .insert({
          equipment_type: data.type,
          equipment_name: data.name,
          equipment_brand: data.brand || null,
          equipment_category: data.category || null,
          use_case: data.useCase || null,
          user_id: userId,
        });
    }

    toast.success(`Thanks! Your recommendation for "${data.name}" has been submitted.`);
  } catch {
    toast.error('Failed to submit recommendation');
  }
}

// Create support ticket from chatbot
async function createSupportTicket(data: CreateTicketData) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast.error('Please sign in to create a support ticket');
      return;
    }

    const { error } = await supabase
      .from('support_tickets')
      .insert({
        user_id: session.user.id,
        subject: data.subject,
        description: data.description,
        priority: 'medium',
      });

    if (error) throw error;

    toast.success('Support ticket created! Check the Support page to track it.');
  } catch {
    toast.error('Failed to create support ticket');
  }
}

// Track saved suggestions to prevent duplicates within a session
const savedSuggestionKeys = new Set<string>();

function normalizeForDedup(str: string): string {
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
}

function getSuggestionKey(data: RentalHouseSuggestionData): string {
  return `${normalizeForDedup(data.name)}|${normalizeForDedup(data.url)}|${normalizeForDedup(data.country)}`;
}

// Save rental house suggestion to database
async function saveRentalHouseSuggestion(data: RentalHouseSuggestionData) {
  const key = getSuggestionKey(data);
  
  // Prevent duplicate saves within the same session
  if (savedSuggestionKeys.has(key)) {
    console.log('[RentalSuggestion] Already saved this session, skipping:', key);
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id || null;

    console.log('[RentalSuggestion] Saving suggestion:', { name: data.name, url: data.url, country: data.country, userId });

    // Normalize URL - ensure it has a protocol
    let normalizedUrl = data.url.trim();
    if (normalizedUrl && !normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'https://' + normalizedUrl;
    }

    // Check if this rental house already exists in suggestions (to increment count)
    // Use normalized name for matching (URL might be empty or vary)
    const normalizedName = normalizeForDedup(data.name);
    
    // Build query - only include URL in search if it's not empty
    let query = supabase
      .from('rental_house_suggestions')
      .select('id, request_count, rental_house_name');
    
    if (normalizedUrl) {
      // If we have a URL, match by URL OR name
      query = query.or(`rental_house_url.ilike.%${normalizedUrl}%,rental_house_name.ilike.%${normalizedName}%`);
    } else {
      // If no URL, just match by name
      query = query.ilike('rental_house_name', `%${normalizedName}%`);
    }
    
    const { data: existing, error: existingError } = await query.limit(1).maybeSingle();

    if (existingError) {
      console.error('[RentalSuggestion] Error checking existing:', existingError);
    }

    if (existing) {
      // Increment request count for existing suggestion
      const { error: updateError } = await supabase
        .from('rental_house_suggestions')
        .update({ 
          request_count: (existing.request_count ?? 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id);
      
      if (updateError) {
        console.error('[RentalSuggestion] Error updating count:', updateError);
        throw updateError;
      }
      console.log('[RentalSuggestion] Incremented count for existing suggestion:', existing.id);
      savedSuggestionKeys.add(key);
      toast.success(`Thanks! Your vote for "${existing.rental_house_name}" has been recorded.`);
    } else {
      // Insert new suggestion
      const { error: insertError } = await supabase
        .from('rental_house_suggestions')
        .insert({
          rental_house_name: data.name,
          rental_house_url: normalizedUrl,
          country: data.country,
          user_id: userId,
        });
      
      if (insertError) {
        console.error('[RentalSuggestion] Error inserting:', insertError);
        throw insertError;
      }
      console.log('[RentalSuggestion] Created new suggestion');
      savedSuggestionKeys.add(key);
      toast.success(`Thanks! Your suggestion for "${data.name}" has been submitted.`);
    }
  } catch (err) {
    console.error('[RentalSuggestion] Failed:', err);
    toast.error('Failed to submit rental house suggestion');
  }
}

export function useRigAssistant() {
  const [messages, setMessages] = useState<Message[]>(loadMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSaveRig, setPendingSaveRig] = useState<SaveRigData | null>(null);
  const { canSendAiMessage, incrementAiMessageCount, maxAiMessages, currentAiMessageCount } = usePlanLimits();
  const streamContentRef = useRef('');

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  const sendMessage = useCallback(async (input: string, context?: RigContext) => {
    if (!input.trim()) return;

    // Check if this is a system function message (recommendations, tickets, etc.)
    // These are FREE and don't count toward the user's message limit
    const isSystemFunc = isSystemFunctionMessage(input, messages);

    // Check if user can send AI message (allow system functions always)
    if (!isSystemFunc && !canSendAiMessage()) {
      toast.error('AI message limit reached. Upgrade your plan for more messages.');
      return;
    }

    const userMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);
    streamContentRef.current = '';

    try {
      // Get user's session token for authenticated requests
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      // Truncate conversation history to avoid exceeding API limit (max 50 messages)
      // Keep the most recent messages, leaving room for the new user message
      const MAX_HISTORY = 48;
      const allMessages = [...messages, userMessage];
      const truncatedMessages = allMessages.length > MAX_HISTORY 
        ? allMessages.slice(-MAX_HISTORY) 
        : allMessages;

      const response = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          messages: truncatedMessages,
          context,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Error: ${response.status}`;
        
        if (response.status === 429) {
          toast.error('Rate limit exceeded. Please wait a moment.');
        } else if (response.status === 402) {
          toast.error('AI usage limit reached. Please add credits.');
        } else {
          toast.error(errorMessage);
        }
        
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Add empty assistant message to start streaming into
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              streamContentRef.current += content;
              const currentContent = streamContentRef.current;
              // Use requestAnimationFrame for smoother updates
              requestAnimationFrame(() => {
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIndex = updated.length - 1;
                  if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
                    updated[lastIndex] = { ...updated[lastIndex], content: currentContent };
                  }
                  return updated;
                });
              });
            }
          } catch {
            // Incomplete JSON, put back and wait for more
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        for (const raw of buffer.split('\n')) {
          if (!raw || !raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              streamContentRef.current += content;
              const currentContent = streamContentRef.current;
              requestAnimationFrame(() => {
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIndex = updated.length - 1;
                  if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
                    updated[lastIndex] = { ...updated[lastIndex], content: currentContent };
                  }
                  return updated;
                });
              });
            }
          } catch { /* ignore */ }
        }
      }

      // Check for save rig command in final content
      const saveData = parseSaveRigCommand(streamContentRef.current);
      if (saveData) {
        setPendingSaveRig(saveData);
      }

      // Check for real-time ADD_CONNECTION commands (when viewing a rig)
      const connectionCommands = parseAllAddConnectionCommands(streamContentRef.current);
      if (connectionCommands.length > 0 && context?.activeRigId) {
        // Dispatch event for each connection to RigView
        connectionCommands.forEach((connection, index) => {
          // Stagger slightly to avoid race conditions
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('ai-add-connection', {
              detail: { connection }
            }));
          }, index * 100);
        });
      }

      // Check for recommend equipment command
      const recommendData = parseRecommendEquipmentCommand(streamContentRef.current);
      if (recommendData) {
        await saveEquipmentRecommendation(recommendData);
      }

      // Check for rental house suggestion command
      const rentalSuggestionData = parseRentalSuggestionCommand(streamContentRef.current);
      if (rentalSuggestionData) {
        await saveRentalHouseSuggestion(rentalSuggestionData);
      }

      // Ticket creation is handled EXCLUSIVELY by UI component via TicketOfferCard button click
      // The [CREATE_TICKET:...] command is NOT auto-executed to prevent duplicate tickets
      // Users must click the "Create Ticket" button in the UI to create a ticket

      // Only increment AI message count for non-system-function messages
      // System functions (recommendations, tickets) are FREE
      if (!isSystemFunc) {
        await incrementAiMessageCount();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [messages, canSendAiMessage, incrementAiMessageCount]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setPendingSaveRig(null);
  }, []);

  const clearPendingSaveRig = useCallback(() => {
    setPendingSaveRig(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    maxAiMessages,
    currentAiMessageCount,
    canSendAiMessage,
    pendingSaveRig,
    clearPendingSaveRig,
  };
}
